import { useMemo } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';
import type { Collaborator } from '../../types/api';

interface CollaboratorsFacultyChartProps {
  collaborators: Collaborator[];
}

interface FacultyBucket {
  key: string;
  abbr: string;
  count: number;
  fill: string;
}

export default function CollaboratorsFacultyChart({
  collaborators,
}: CollaboratorsFacultyChartProps) {
  const data = useMemo<FacultyBucket[]>(() => {
    if (collaborators.length === 0) return [];
    const counts = new Map<string, number>();
    for (const c of collaborators) {
      const key = (c.faculty_abbr || 'Other').trim() || 'Other';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([abbr, count]) => ({
        key: abbr,
        abbr,
        count,
        fill: FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR,
      }))
      .sort((a, b) => b.count - a.count);
  }, [collaborators]);

  const total = useMemo(() => data.reduce((s, d) => s + d.count, 0), [data]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = { count: { label: 'Faculty' } };
    for (const d of data) cfg[d.key] = { label: d.abbr, color: d.fill };
    return cfg;
  }, [data]);

  if (data.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-center">
      <ChartContainer
        config={chartConfig}
        className="aspect-square w-full max-w-[260px] shrink-0"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                nameKey="abbr"
                hideLabel
                formatter={(value: any, _name: any, item: any) => {
                  const pct = total ? Math.round((Number(value) / total) * 100) : 0;
                  return (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="flex items-center gap-2">
                        <span
                          className="size-2 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: item?.payload?.fill }}
                        />
                        <span>{item?.payload?.abbr}</span>
                      </span>
                      <span className="font-mono tabular-nums text-foreground">
                        {value} <span className="text-ctu-text-muted">({pct}%)</span>
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          <Pie
            data={data}
            dataKey="count"
            nameKey="abbr"
            innerRadius="55%"
            outerRadius="92%"
            paddingAngle={1.5}
            strokeWidth={2}
            stroke="hsl(var(--card))"
          >
            {data.map(d => (
              <Cell key={d.key} fill={d.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>

      <ul className="m-0 flex w-full flex-col divide-y divide-border list-none p-0 sm:max-w-[220px]">
        {data.map(d => {
          const pct = total ? Math.round((d.count / total) * 100) : 0;
          return (
            <li key={d.key} className="flex items-center gap-3 py-2 text-sm">
              <span
                className="size-2.5 shrink-0 rounded-[3px]"
                style={{ backgroundColor: d.fill }}
                aria-hidden
              />
              <span className="flex-1 min-w-0 truncate text-ctu-text">{d.abbr}</span>
              <span className="font-mono tabular-nums text-ctu-text-muted text-xs">
                {d.count} ({pct}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
